import type { LucideProps } from "lucide-react"
import {
  CheckCircle2,
  Clock,
  Database,
  FileText,
  FolderOpen,
  Globe,
  Link,
  Loader2,
  Mail,
  Search,
  Send,
  Settings,
  User,
  Zap,
} from "lucide-react"
import type { ComponentType } from "react"

import { cn } from "@/lib/utils"

type ActionStatus = "completed" | "active" | "pending"

export interface ActionItem {
  label: string
  status: ActionStatus
}

export type AgentStatusLoaderState = "thinking" | "working" | "done"

export interface AgentStatusLoaderProps {
  state: AgentStatusLoaderState
  actions?: ActionItem[]
  className?: string
}

const iconKeywords: [string[], ComponentType<LucideProps>][] = [
  [["form", "document", "read", "file", "report", "pdf"], FileText],
  [["search", "find", "lookup", "match", "extract", "query"], Search],
  [["contact", "person", "user", "client", "team", "member"], User],
  [["folder", "directory", "project", "workspace"], FolderOpen],
  [["send", "submit", "push", "post", "publish"], Send],
  [["link", "connect", "url", "integrate"], Link],
  [["automat", "trigger", "run", "execut", "process"], Zap],
  [["data", "record", "database", "table", "store"], Database],
  [["setting", "config", "option", "preference"], Settings],
  [["email", "mail", "message", "notify", "notification"], Mail],
  [["wait", "schedule", "time", "delay", "queue"], Clock],
  [["web", "api", "fetch", "request", "endpoint"], Globe],
]

function inferIcon(label: string): ComponentType<LucideProps> {
  const lower = label.toLowerCase()
  for (const [keywords, icon] of iconKeywords) {
    if (keywords.some((kw) => lower.includes(kw))) {
      return icon
    }
  }
  return Zap
}

function ActionIcon({
  icon: IconComponent,
  isActive,
  isCompleted,
}: {
  icon: ComponentType<LucideProps>
  isActive: boolean
  isCompleted: boolean
}) {
  return (
    // The vertical rail runs the full height behind the list; the bg + ring fill
    // masks it so the rail appears to stop at each icon instead of bleeding
    // through it. Uses bg-background/ring-background so it works in both themes.
    <span className="bg-background ring-background flex size-4 shrink-0 items-center justify-center rounded-full ring-4">
      <IconComponent
        className={cn(
          "size-4 shrink-0",
          isCompleted && "text-muted-foreground",
          isActive && "text-secondary",
          !isCompleted && !isActive && "text-muted-foreground",
        )}
      />
    </span>
  )
}

export function AgentStatusLoader({
  state,
  actions = [],
  className,
}: AgentStatusLoaderProps) {
  const isThinking = state === "thinking"
  const isDone = state === "done"
  const showHeader = !isDone

  return (
    <div data-slot="agent-status-loader" className={cn("w-full", className)}>
      {showHeader && (
        <div className="flex items-center gap-2 px-1 py-2">
          <Loader2 className="text-muted-foreground size-4 animate-spin" />
          <span className="text-sm font-medium">
            {isThinking ? "Thinking" : "Working"}
          </span>
        </div>
      )}

      {actions.length > 0 && (
        <div className="relative mt-1 ml-1">
          {/* Vertical rail */}
          <div className="border-border absolute top-0 bottom-0 left-[7px] w-px border-l" />

          <div className="flex flex-col">
            {actions.map((action, index) => {
              const isActive = action.status === "active"
              const isCompleted = action.status === "completed" || isDone
              const IconComp = inferIcon(action.label)
              const isLast = index === actions.length - 1

              return (
                <div key={index} className="flex flex-col">
                  <div className="relative z-10 flex items-center gap-3 py-2">
                    <ActionIcon
                      icon={IconComp}
                      isActive={isActive && !isDone}
                      isCompleted={isCompleted}
                    />
                    <span
                      className={cn(
                        "text-sm",
                        isCompleted && "text-muted-foreground",
                        isActive && !isDone && "agent-shimmer text-foreground",
                        !isCompleted && !isActive && "text-muted-foreground",
                      )}
                    >
                      {action.label}
                    </span>
                  </div>

                  {/* Connector segment between items */}
                  {!isLast && <div className="h-1" />}
                </div>
              )
            })}

            {/* Done milestone — same masked-icon treatment so the rail stops
                at the checkmark rather than running through it. */}
            {isDone && (
              <div className="relative z-10 flex items-center gap-3 py-2">
                <ActionIcon icon={CheckCircle2} isActive={false} isCompleted />
                <span className="text-muted-foreground text-sm">Done</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
